import type {TextProjection} from './annotationTextProjection';
import {ANNOTATION_REGION_ATTRIBUTE, isMathJaxElement} from './annotationTextProjection';
import type {Annotation, AnnotationRange} from '../types';

/** Set on every `<mark>` this module adds; styles and hit tests key off it. */
export const ANNOTATION_MARK_ATTRIBUTE = 'data-annotation-mark';
export const ANNOTATION_MARK_ID_ATTRIBUTE = 'data-annotation-id';
/** Read out by assistive tech through the mark's `::before` / `::after` (annotations-highlights.scss). */
export const ANNOTATION_MARK_START_ATTRIBUTE = 'data-annotation-mark-start';
export const ANNOTATION_MARK_END_ATTRIBUTE = 'data-annotation-mark-end';

/** One annotation to expose, with the normalized range it resolved to and its text. */
export interface ResolvedMark {
  annotation: Annotation;
  range: AnnotationRange;
  text: string;
}

interface MarkPiece {
  node: Text;
  start: number;
  end: number;
  mark: ResolvedMark;
  isFirst: boolean;
  isLast: boolean;
  /** The outermost formula element holding `node`; math is never wrapped. */
  math: Element | null;
  /** The first mark to start in its answer control, which Enter on that control opens. */
  opensFromControl?: boolean;
}

/**
 * A React-owned Text node this module emptied. The node itself never moves; its
 * text lives in `inserted` until it is put back.
 */
interface SplitHost {
  host: Text;
  original: string;
  inserted: Node[];
}

interface RegionWrap {
  hosts: SplitHost[];
  /** Empty marks beside formulas, where a mark starts or ends inside the math. */
  mathBoundaries: Node[];
}

// React keeps the Text nodes it rendered and later rewrites or removes them in place;
// moving one into a <mark> breaks that commit, so hosts are only ever emptied.
const ownNodes = new WeakSet<Node>();
const ownText = new WeakMap<Node, string>();
const expectedHostText = new WeakMap<Text, string>();
const wrapsByContainer = new WeakMap<Element, RegionWrap>();

// No `label`: it has no widget role, so a mark in answer text keeps its focus stop.
const INTERACTIVE_SELECTOR = [
  'a[href]',
  'button',
  'summary',
  'input',
  'select',
  'textarea',
  '[role="button"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="option"]',
  '[role="switch"]',
  '[role="tab"]',
  '[role="link"]'
].join(', ');

const styleName = (annotation: Annotation): string =>
  annotation.type === 'underline' ? 'underline' : `${annotation.color} highlight`;

/**
 * What assistive tech hears where a mark starts, matching the editor's swatch labels.
 * The note rides along, so read-only views (review, teacher) still reach it.
 */
export const describeMarkStart = (annotation: Annotation): string =>
  annotation.note
    ? `Start of ${styleName(annotation)}, note: ${annotation.note}.`
    : `Start of ${styleName(annotation)}.`;

export const describeMarkEnd = (annotation: Annotation): string =>
  `End of ${styleName(annotation)}.`;

/** The name of a mark's focus stop, which opens its editor. */
export const describeMarkControl = (annotation: Annotation, text: string): string =>
  `Edit ${styleName(annotation)}${annotation.note ? ' with note' : ''}: ${text}`;

const SUPPLEMENT_NOUN_BY_TYPE: Readonly<Record<string, string>> = {
  image: 'Image',
  audio: 'Audio',
  'free-form': 'Document'
};

/** What assistive tech hears on a whole-supplement mark, naming what was marked. */
export const describeSupplementMark = (annotation: Annotation, supplementType: string): string => {
  const marked = `${SUPPLEMENT_NOUN_BY_TYPE[supplementType] ?? 'Supplement'} marked with ${styleName(annotation)}`;
  return annotation.note ? `${marked}, note: ${annotation.note}.` : `${marked}.`;
};

/**
 * True for a mutation this module made, so the region's observers skip it instead
 * of clearing and repainting in a loop.
 */
export const isOwnMarkMutation = (record: MutationRecord): boolean => {
  if (record.type === 'characterData') {
    return (
      record.target instanceof Text && expectedHostText.get(record.target) === record.target.data
    );
  }
  if (record.type === 'attributes') {
    return ownNodes.has(record.target);
  }
  const touched = [...Array.from(record.addedNodes), ...Array.from(record.removedNodes)];
  return touched.length > 0 && touched.every((node) => ownNodes.has(node));
};

// MathJax owns its glyph markup and assistive MathML; a <mark> inside either breaks it.
const outermostMathOf = (node: Node, container: Element): Element | null => {
  let math: Element | null = null;
  for (let element = node.parentElement; element && element !== container;) {
    if (
      isMathJaxElement(element) ||
      element.tagName.toLowerCase() === 'math' ||
      /(^|\s)MathJax/.test(element.getAttribute('class') ?? '')
    ) {
      math = element;
    }
    element = element.parentElement;
  }
  return math;
};

// MathJax v2 finds a formula's output as the previous sibling of its source script,
// so nothing may sit between the two.
const lastNodeOfMath = (math: Element): Node => {
  const next = math.nextSibling;
  return next instanceof HTMLScriptElement && next.type.startsWith('math/') ? next : math;
};

const controlHolding = (node: Node): Element | null =>
  node.parentElement?.closest(INTERACTIVE_SELECTOR) ?? null;

// A focus stop inside an answer control would nest one control in another, whether the
// control sits in the region or holds it (a draggable chip).
const isInsideInteractive = (node: Node): boolean => Boolean(controlHolding(node));

const comparePieces = (a: MarkPiece, b: MarkPiece): number => {
  if (a.node !== b.node) {
    return a.node.compareDocumentPosition(b.node) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
  }
  return a.start - b.start;
};

/** Drops pieces overlapping one already kept in the same node, then marks each mark's ends. */
const keepFirstOfOverlaps = (pieces: MarkPiece[]): MarkPiece[] => {
  const keptEndByNode = new Map<Text, number>();
  const kept = pieces.filter((piece) => {
    if (piece.start < (keptEndByNode.get(piece.node) ?? -1)) {
      return false;
    }
    keptEndByNode.set(piece.node, piece.end);
    return true;
  });
  const byMark = new Map<ResolvedMark, MarkPiece[]>();
  kept.forEach((piece) => byMark.set(piece.mark, [...(byMark.get(piece.mark) ?? []), piece]));
  byMark.forEach((own) => {
    own[0].isFirst = true;
    own[own.length - 1].isLast = true;
  });
  return kept;
};

/**
 * Each mark's text per Text node, in document order. A mark's runs inside one node
 * merge across the whitespace the projection collapsed; whitespace-only runs (the
 * space between two blocks) are left unwrapped.
 */
const collectPieces = (
  projection: TextProjection,
  marks: ResolvedMark[],
  container: Element
): MarkPiece[] => {
  const pieces: MarkPiece[] = [];
  marks.forEach((mark) => {
    const {range} = mark;
    const own: MarkPiece[] = [];
    projection.segments.forEach((segment) => {
      const segmentEnd = segment.normStart + segment.length;
      if (segment.normStart >= range.end || segmentEnd <= range.start) {
        return;
      }
      const start = segment.nodeStart + Math.max(range.start - segment.normStart, 0);
      const end = segment.nodeStart + Math.min(range.end, segmentEnd) - segment.normStart;
      const previous = own[own.length - 1];
      if (previous?.node === segment.node && !segment.node.data.slice(previous.end, start).trim()) {
        previous.end = end;
        return;
      }
      own.push({
        node: segment.node,
        start,
        end,
        mark,
        isFirst: false,
        isLast: false,
        math: outermostMathOf(segment.node, container)
      });
    });
    pieces.push(...own.filter((piece) => piece.node.data.slice(piece.start, piece.end).trim()));
  });
  return keepFirstOfOverlaps(pieces.sort(comparePieces));
};

const ownTextNode = (text: string): Text => {
  const node = document.createTextNode(text);
  ownNodes.add(node);
  ownText.set(node, text);
  return node;
};

const createMark = (piece: MarkPiece, text: string, focusable: boolean): HTMLElement => {
  const {annotation} = piece.mark;
  const mark = document.createElement('mark');
  mark.setAttribute(ANNOTATION_MARK_ATTRIBUTE, '');
  mark.setAttribute(ANNOTATION_MARK_ID_ATTRIBUTE, annotation.id);
  if (piece.isFirst) {
    const start = describeMarkStart(annotation);
    mark.setAttribute(
      ANNOTATION_MARK_START_ATTRIBUTE,
      piece.opensFromControl ? `${start} Press Enter to edit.` : start
    );
    if (focusable && !isInsideInteractive(piece.node)) {
      mark.tabIndex = 0;
      mark.setAttribute('role', 'button');
      mark.setAttribute('aria-haspopup', 'dialog');
      mark.setAttribute('aria-label', describeMarkControl(annotation, piece.mark.text));
    }
  }
  if (piece.isLast) {
    mark.setAttribute(ANNOTATION_MARK_END_ATTRIBUTE, describeMarkEnd(annotation));
  }
  if (text) {
    mark.appendChild(ownTextNode(text));
  }
  ownNodes.add(mark);
  ownText.set(mark, text);
  return mark;
};

const splitHost = (host: Text, pieces: MarkPiece[], focusable: boolean): SplitHost | null => {
  const parent = host.parentNode;
  if (!parent) {
    return null;
  }
  const original = host.data;
  const inserted: Node[] = [];
  const fragment = document.createDocumentFragment();
  const append = (node: Node) => {
    fragment.appendChild(node);
    inserted.push(node);
  };
  const lead = original.slice(0, pieces[0].start);
  if (lead) {
    append(ownTextNode(lead));
  }
  pieces.forEach((piece, index) => {
    append(createMark(piece, original.slice(piece.start, piece.end), focusable));
    const gap = original.slice(piece.end, pieces[index + 1]?.start ?? original.length);
    if (gap) {
      append(ownTextNode(gap));
    }
  });
  expectedHostText.set(host, '');
  host.data = '';
  parent.insertBefore(fragment, host.nextSibling);
  return {host, original, inserted};
};

/**
 * A mark that starts or ends inside a formula gets that edge as an empty `<mark>`
 * just before or after the formula.
 */
const insertMathBoundaries = (pieces: MarkPiece[], focusable: boolean): Node[] => {
  const inserted: Node[] = [];
  const lastAfterMath = new Map<Element, Node>();
  pieces.forEach((piece) => {
    const {math} = piece;
    if (!math?.parentNode) {
      return;
    }
    if (piece.isFirst) {
      const boundary = createMark({...piece, isLast: false}, '', focusable);
      math.parentNode.insertBefore(boundary, math);
      inserted.push(boundary);
    }
    if (piece.isLast) {
      const boundary = createMark({...piece, isFirst: false}, '', focusable);
      const after = lastAfterMath.get(math) ?? lastNodeOfMath(math);
      after.parentNode?.insertBefore(boundary, after.nextSibling);
      lastAfterMath.set(math, boundary);
      inserted.push(boundary);
    }
  });
  return inserted;
};

const flagFirstMarkPerControl = (pieces: MarkPiece[]): MarkPiece[] => {
  const flagged = new Set<Element>();
  return pieces.map((piece) => {
    const control = piece.isFirst ? controlHolding(piece.node) : null;
    if (!control || flagged.has(control)) {
      return piece;
    }
    flagged.add(control);
    return {...piece, opensFromControl: true};
  });
};

/**
 * Wraps each mark's text in `<mark>` elements so assistive tech can find it. The
 * visible paint stays with the CSS Custom Highlight API; these add no styling.
 * The projection must describe unwrapped DOM, so call `unwrapRegionMarks` first.
 */
export const wrapRegionMarks = (
  container: Element,
  projection: TextProjection,
  marks: ResolvedMark[],
  {focusable}: {focusable: boolean}
): void => {
  const collected = collectPieces(projection, marks, container);
  const pieces = focusable ? flagFirstMarkPerControl(collected) : collected;
  const piecesByNode = new Map<Text, MarkPiece[]>();
  pieces.forEach((piece) => {
    if (!piece.math) {
      piecesByNode.set(piece.node, [...(piecesByNode.get(piece.node) ?? []), piece]);
    }
  });
  const hosts: SplitHost[] = [];
  piecesByNode.forEach((nodePieces, node) => {
    const split = splitHost(node, nodePieces, focusable);
    if (split) {
      hosts.push(split);
    }
  });
  const mathBoundaries = insertMathBoundaries(
    pieces.filter((piece) => piece.math),
    focusable
  );
  if (hosts.length > 0 || mathBoundaries.length > 0) {
    wrapsByContainer.set(container, {hosts, mathBoundaries});
  }
};

/** True while this region has `<mark>` elements in its DOM. */
export const isRegionWrapped = (container: Element): boolean => wrapsByContainer.has(container);

const isPristine = (node: Node): boolean =>
  node instanceof Text
    ? node.data === ownText.get(node)
    : node.childNodes.length === 1 &&
      ownNodes.has(node.firstChild as Node) &&
      node.textContent === ownText.get(node);

const unwrapInPlace = (node: Node): void => {
  const parent = node.parentNode;
  if (!parent || !(node instanceof Element)) {
    return;
  }
  while (node.firstChild) {
    parent.insertBefore(node.firstChild, node);
  }
  parent.removeChild(node);
};

/**
 * Puts the region's text back the way React rendered it. A host React has since
 * rewritten or removed stays as React left it; only this module's nodes go.
 */
export const unwrapRegionMarks = (container: Element): void => {
  const wrap = wrapsByContainer.get(container);
  if (!wrap) {
    return;
  }
  wrapsByContainer.delete(container);
  wrap.mathBoundaries.forEach((node) => node.parentNode?.removeChild(node));
  wrap.hosts.forEach(({host, original, inserted}) => {
    // Something other than React (page translation, a reading extension) rewrote this
    // text; what the page shows now stays, and only the <mark> wrappers go.
    if (!inserted.every(isPristine)) {
      inserted.forEach(unwrapInPlace);
      return;
    }
    inserted.forEach((node) => node.parentNode?.removeChild(node));
    if (host.isConnected && expectedHostText.get(host) === host.data) {
      expectedHostText.set(host, original);
      host.data = original;
    }
  });
};

/** This region's own `<mark>` holding `target`; a nested region's marks are its own. */
export const markElementOf = (
  target: EventTarget | null,
  container: Element
): HTMLElement | null => {
  if (!(target instanceof Element)) {
    return null;
  }
  const mark = target.closest<HTMLElement>(`mark[${ANNOTATION_MARK_ATTRIBUTE}]`);
  if (!mark || !container.contains(mark)) {
    return null;
  }
  for (let element = mark.parentElement; element && element !== container;) {
    if (element.hasAttribute(ANNOTATION_REGION_ATTRIBUTE)) {
      return null;
    }
    element = element.parentElement;
  }
  return mark;
};

/** The annotation id of this region's `<mark>` holding `target`, if any. */
export const annotationIdOfMark = (target: EventTarget | null, container: Element): string | null =>
  markElementOf(target, container)?.getAttribute(ANNOTATION_MARK_ID_ATTRIBUTE) ?? null;

/**
 * The mark that activating an answer control (a two-way cell, a chip) opens, since its
 * own action owns the marks' focus stop: the first of this region's marks to start in it.
 */
export const markOpenedByControl = (
  target: EventTarget | null,
  container: Element
): HTMLElement | null => {
  const control = target instanceof Element ? target.closest(INTERACTIVE_SELECTOR) : null;
  if (!control || !(control.contains(container) || container.contains(control))) {
    return null;
  }
  return (
    Array.from(
      container.querySelectorAll<HTMLElement>(
        `mark[${ANNOTATION_MARK_ATTRIBUTE}][${ANNOTATION_MARK_START_ATTRIBUTE}]`
      )
    ).find((mark) => markElementOf(mark, container) === mark && controlHolding(mark) === control) ??
    null
  );
};

/** The focus stop of `annotationId`'s mark in this region, if it is rendered. */
export const findMarkFocusStop = (container: Element, annotationId: string): HTMLElement | null =>
  Array.from(
    container.querySelectorAll<HTMLElement>(`mark[${ANNOTATION_MARK_ATTRIBUTE}][tabindex]`)
  ).find(
    (mark) =>
      mark.getAttribute(ANNOTATION_MARK_ID_ATTRIBUTE) === annotationId &&
      markElementOf(mark, container) === mark
  ) ?? null;
